import { IMPORT_TARGET } from '../../../domains/people-access/constants/importTargets.js';
import {
  ALREADY_INVITED_STATUSES,
  INVITE_MODE,
  UNJOINED_MEMBER_STATUSES,
} from '../../../domains/people-access/constants/inviteModes.js';
import { ISSUE, REVIEW_STATUS_FILTER, ROW_STATUS } from '../../../domains/people-access/constants/reviewFilters.js';
import { applyReviewFilters, countByStatus } from '../../../domains/people-access/utils/reviewFilters.js';

const EXAMPLES_PER_COLUMN = 2;

/** Which targets are in use (skipped columns do not count). */
const usedTargets = (state) => new Set(Object.values(state.mapping));

/**
 * What blocks Next on the match step: `[]`, or any of `'email_missing'` / `'name_missing'`.
 * The minimum is an email plus a first name or a full name; a last name alone is not enough.
 */
export function selectMappingErrors(state) {
  const used = usedTargets(state);
  const errors = [];
  if (!used.has(IMPORT_TARGET.EMAIL)) errors.push('email_missing');
  if (!used.has(IMPORT_TARGET.FIRST_NAME) && !used.has(IMPORT_TARGET.FULL_NAME)) {
    errors.push('name_missing');
  }
  return errors;
}

/** The column that uses `target`, for the `Used by "X"` label, or `null`. */
export function selectTargetOwner(state, target) {
  if (target === IMPORT_TARGET.SKIP) return null;
  return Object.entries(state.mapping).find(([, mapped]) => mapped === target)?.[0] ?? null;
}

/** Columns left out of the import, in file order. */
export function selectNotImported(state) {
  return (state.file?.headers ?? []).filter(
    (header) => (state.mapping[header] ?? IMPORT_TARGET.SKIP) === IMPORT_TARGET.SKIP,
  );
}

/** Up to two distinct, filled sample values per column, aligned with `file.headers`. */
export function selectColumnExamples(state) {
  return (state.file?.headers ?? []).map((_, column) => {
    const examples = [];
    for (const row of state.rawRows) {
      const value = row.cells[column];
      if (value && !examples.includes(value)) examples.push(value);
      if (examples.length === EXAMPLES_PER_COLUMN) break;
    }
    return examples;
  });
}

/** Rows the table shows: search + the four dropdowns + the status chip. */
export function selectVisibleRows(state) {
  const { search, filters } = state.review;
  return applyReviewFilters(state.rows, filters, state.catalog, search);
}

/** All / New / Updates / Errors, following search and dropdowns but not the status chip. */
export function selectCounters(state) {
  const { search, filters } = state.review;
  return countByStatus(
    applyReviewFilters(state.rows, { ...filters, status: REVIEW_STATUS_FILTER.ALL }, state.catalog, search),
  );
}

/** Lines under the filters: new people without access, and what the list leaves out. */
export function selectSummary(state) {
  const fresh = state.rows.filter((row) => row.status === ROW_STATUS.NEW);
  return {
    newTotal: fresh.length,
    newWithoutAccess: fresh.filter((row) => row.access === null).length,
    unchanged: state.notListed.unchanged,
  };
}

/**
 * Selection of the review table. `pageRowNumbers` are the rows on the current page; `allVisible`
 * says every row that passes the filters (all pages) is selected.
 */
export function selectSelectionState(state, pageRowNumbers) {
  const selected = new Set(state.review.selection);
  const onPage = pageRowNumbers.filter((n) => selected.has(n)).length;
  const visible = selectVisibleRows(state);
  return {
    count: selected.size,
    allOnPage: pageRowNumbers.length > 0 && onPage === pageRowNumbers.length,
    someOnPage: onPage > 0,
    allVisible: visible.length > 0 && visible.every((row) => selected.has(row.rowNumber)),
  };
}

const rowsWithStatus = (state, status) => state.rows.filter((row) => row.status === status);
const studiosOf = (row, roles) =>
  new Set((row.access?.role_ids ?? []).flatMap((id) => roles.find((r) => r.id === id)?.studios ?? []));

const isSameEmail = (row) =>
  row.issues.some((issue) => issue.code === ISSUE.POSSIBLE_DUPLICATE && issue.sameEmail);

/**
 * What must be handled before the primary button enables: Admins to confirm, possible
 * duplicates and people outside the workspace domain. Only people who would join the workspace
 * count: rows with errors are not added and existing members only get their profile updated, so
 * neither blocks. A repeated email is the exception. It is one person, so every extra copy
 * blocks even when the rows would only update that member. Otherwise two updates are sent.
 */
export function selectCheckBlockers(state) {
  const adding = rowsWithStatus(state, ROW_STATUS.NEW);
  const admins = adding.filter((row) => row.access?.user_type === 'admin' && !row.adminConfirmed);
  const duplicates = [
    ...adding.filter(
      (row) => !isSameEmail(row) && row.issues.some((issue) => issue.code === ISSUE.POSSIBLE_DUPLICATE),
    ),
    ...state.rows.filter((row) => row.status !== ROW_STATUS.ERROR && isSameEmail(row)),
  ];
  const outsideDomain = adding.filter(
    (row) => row.issues.some((issue) => issue.code === ISSUE.OUTSIDE_DOMAIN) && !row.domainConfirmed,
  );
  // Kept on purpose: they stay in the list with an Undo, but nothing is left to decide.
  const keptOutside = adding.filter(
    (row) => row.issues.some((issue) => issue.code === ISSUE.OUTSIDE_DOMAIN) && row.domainConfirmed,
  );
  return {
    admins,
    duplicates,
    outsideDomain,
    keptOutside,
    total: admins.length + duplicates.length + outsideDomain.length,
  };
}

const memberOf = (state, row) => state.catalog.members.find((m) => m.id === row.existingMemberId);

/** Numbers of the summary card of step 4. */
export function selectCheckSummary(state) {
  const fresh = rowsWithStatus(state, ROW_STATUS.NEW);
  const updates = rowsWithStatus(state, ROW_STATUS.UPDATE);
  const byStudio = {};
  fresh.forEach((row) =>
    studiosOf(row, state.catalog.roles).forEach((studio) => {
      byStudio[studio] = (byStudio[studio] ?? 0) + 1;
    }),
  );
  return {
    ready: fresh.length,
    updates: updates.length,
    excluded: rowsWithStatus(state, ROW_STATUS.ERROR).length + state.notListed.removed,
    byStudio,
    withoutStudio: fresh.filter(
      (row) => row.access?.user_type !== 'admin' && studiosOf(row, state.catalog.roles).size === 0,
    ).length,
    locations: [...new Set(fresh.map((row) => row.person.location).filter(Boolean))].sort(),
    alreadyInvited: updates.filter((row) => ALREADY_INVITED_STATUSES.includes(memberOf(state, row)?.status))
      .length,
  };
}

/** How many people each invitation option would reach. */
export function selectInviteCounts(state) {
  const fresh = rowsWithStatus(state, ROW_STATUS.NEW);
  const freshIds = new Set(fresh.map((row) => row.rowNumber));
  return {
    all: fresh.length,
    withAccess: fresh.filter((row) => row.access !== null).length,
    selected: state.invite.selectedIds.filter((id) => freshIds.has(id)).length,
    updates: rowsWithStatus(state, ROW_STATUS.UPDATE).length,
    existingUnjoined: rowsWithStatus(state, ROW_STATUS.UPDATE).filter((row) =>
      UNJOINED_MEMBER_STATUSES.includes(memberOf(state, row)?.status),
    ).length,
  };
}

/** Names of the existing members of the file who have not joined yet (the "Also invite" option). */
export function selectUnjoinedMemberNames(state) {
  return rowsWithStatus(state, ROW_STATUS.UPDATE)
    .map((row) => memberOf(state, row))
    .filter((member) => UNJOINED_MEMBER_STATUSES.includes(member?.status))
    .map((member) => member.name);
}

/** New people the chosen option invites who have no access yet: they can sign in but open no studio. */
export function selectInviteesWithoutAccess(state) {
  const fresh = rowsWithStatus(state, ROW_STATUS.NEW);
  const { mode, selectedIds } = state.invite;
  const invited = {
    [INVITE_MODE.ALL]: fresh,
    [INVITE_MODE.WITH_ACCESS]: fresh.filter((row) => row.access !== null),
    [INVITE_MODE.SELECTED]: fresh.filter((row) => selectedIds.includes(row.rowNumber)),
  }[mode];
  return (invited ?? []).filter((row) => row.access === null);
}

/** How many invitations the chosen option sends (new people, plus existing members asked again). */
export function selectInviteTotal(state) {
  const counts = selectInviteCounts(state);
  const { mode, reinviteExisting } = state.invite;
  const byMode = {
    [INVITE_MODE.NONE]: 0,
    [INVITE_MODE.ALL]: counts.all,
    [INVITE_MODE.WITH_ACCESS]: counts.withAccess,
    [INVITE_MODE.SELECTED]: counts.selected,
  };
  return (byMode[mode] ?? 0) + (reinviteExisting ? counts.existingUnjoined : 0);
}

/**
 * Which label the primary button takes: `add`, `addAndInvite` or `addInvitePart`; with no new
 * person, only existing members to update: `update` or `updateInvite`.
 */
export function selectPrimaryLabel(state) {
  const counts = selectInviteCounts(state);
  const invite = selectInviteTotal(state);
  if (counts.all === 0 && counts.updates > 0) {
    return invite > 0
      ? { key: 'updateInvite', count: counts.updates, invite }
      : { key: 'update', count: counts.updates };
  }
  if (invite === 0) return { key: 'add', count: counts.all };
  if (invite === counts.all) return { key: 'addAndInvite', count: counts.all };
  return { key: 'addInvitePart', count: counts.all, invite };
}
